import { FC } from "@orgiggly/shared";
import { ErrorBoundary } from "react-error-boundary";
import { captureError } from "../../utils/errorReporting";
import { deriveRoute } from "../../utils/errorReporting.helpers";
import { applyWaitingSwAndReload } from "../../utils/pwa/applyWaitingSwAndReload";
import { isChunkLoadError } from "./routeErrorBoundary.helpers";
import { SiteLoadError } from "./SiteLoadError";

/**
 * The outermost boundary, above `Root`'s lazy-route `<Suspense>` (#1491).
 * Every app mounts its own `AppErrorBoundary`, but that lives INSIDE the lazy
 * chunk — so when `import("./apps/site/SiteApp")` itself rejects, nothing
 * caught it and React unmounted the whole tree: a blank page that reported
 * nothing. This renders the retry screen and reports, tagging a chunk-load
 * failure apart from any render error that escaped the inner boundaries.
 *
 * Retry is a reload, never `resetErrorBoundary()`: React caches a rejected
 * `lazy()` promise, so re-rendering re-throws. `applyWaitingSwAndReload`
 * activates any waiting service worker first, so a stale bundle whose chunks
 * were deleted by a deploy reloads onto the new one.
 */
export const RouteErrorBoundary: FC = ({ children }) => (
  <ErrorBoundary
    onError={(error, info) =>
      captureError(error, {
        failure: isChunkLoadError(error) ? "chunk-load" : "root-render",
        route: deriveRoute(window.location.pathname),
        online: navigator.onLine,
        componentStack: info.componentStack ?? undefined,
      })
    }
    fallbackRender={() => (
      <SiteLoadError onRetry={() => void applyWaitingSwAndReload()} />
    )}
  >
    {children}
  </ErrorBoundary>
);
